import React from 'react'
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom'
import MainLayout from '@/components/layout/MainLayout'
import AuthenticatedLayout from '@/components/layout/AuthenticatedLayout'
import Login from '@/pages/Login'
import Dashboard from '@/pages/Dashboard'
import ServicesManagement from '@/pages/content/ServicesManagement'
import LocationsManagement from '@/pages/content/LocationsManagement'
import BlogManagement from '@/pages/content/BlogManagement'
import StaticPagesManagement from '@/pages/content/StaticPagesManagement'
import ProjectsManagement from '@/pages/content/ProjectsManagement'
import LeadsManagement from '@/pages/customers/LeadsManagement'
import LeadAttributionDashboard from '@/pages/customers/LeadAttributionDashboard'
import ReviewsManagement from '@/pages/integrations/gmb/ReviewsManagement'
import GMBReviews from '@/pages/integrations/gmb/GMBReviews'
import GalleryManagement from '@/pages/media/GalleryManagement'
import GalleryDetailPage from '@/pages/media/GalleryDetailPage'
import RepositoryBrowser from '@/pages/media/RepositoryBrowser'
import ApprovalQueue from '@/pages/media/ApprovalQueue'
import Assets from '@/pages/media/Assets'
import SocialMediaManagerDirect from '@/pages/marketing/SocialMediaManagerDirect'
import CreatePost from '@/pages/marketing/CreatePost'
import ContentCalendar from '@/pages/marketing/ContentCalendar'
import CampaignTracking from '@/pages/marketing/CampaignTracking'
import SocialAnalytics from '@/pages/marketing/SocialAnalytics'
import SystemStatus from '@/pages/marketing/SystemStatus'
import IntegrationsOverview from '@/pages/integrations/IntegrationsOverview'
import JobberManagement from '@/pages/integrations/JobberManagement'
import GMBLayout from '@/pages/integrations/gmb/GMBLayout'
import GMBOverview from '@/pages/integrations/gmb/GMBOverview'
import GMBSetup from '@/pages/integrations/GMBSetup'
import GMBPosts from '@/pages/integrations/gmb/GMBPosts'
import GMBQuestions from '@/pages/integrations/gmb/GMBQuestions'
import GMBSync from '@/pages/integrations/gmb/GMBSync'
import GMBDebug from '@/pages/integrations/gmb/GMBDebug'

// Placeholder pages for features not yet implemented
const PlaceholderPage = ({ title }: { title: string }) => (
  <div className="space-y-6">
    <div>
      <h1 className="text-3xl font-bold tracking-tight">{title}</h1>
      <p className="text-muted-foreground">
        This page is under construction. Features will be implemented in upcoming phases.
      </p>
    </div>
    <div className="bg-muted/20 border-2 border-dashed border-muted rounded-lg p-12 text-center">
      <h3 className="text-lg font-semibold mb-2">Coming Soon</h3>
      <p className="text-muted-foreground">
        {title} functionality will be available once the merger is complete.
      </p>
    </div>
  </div>
)

export default function App() {
  return (
    <Router>
      <Routes>
        {/* Public Routes */}
        <Route path="/login" element={<Login />} />
        
        {/* Protected Routes */}
        <Route 
          path="/" 
          element={
            <AuthenticatedLayout>
              <MainLayout />
            </AuthenticatedLayout>
          }
        >
          <Route index element={<Dashboard />} />
          
          {/* Content Management Routes */}
          <Route path="content">
            <Route path="services" element={<ServicesManagement />} />
            <Route path="locations" element={<LocationsManagement />} />
            <Route path="blog" element={<BlogManagement />} />
            <Route path="pages" element={<StaticPagesManagement />} />
            <Route path="projects" element={<ProjectsManagement />} />
          </Route>

          {/* Media & Gallery Routes */}
          <Route path="media">
            <Route path="galleries" element={<GalleryManagement />} />
            <Route path="galleries/:id" element={<GalleryDetailPage />} />
            <Route path="repository" element={<RepositoryBrowser />} />
            <Route path="approval" element={<ApprovalQueue />} />
            <Route path="assets" element={<Assets />} />
          </Route>

          {/* Customer Operations Routes */}
          <Route path="customers">
            <Route path="leads" element={<LeadsManagement />} />
            <Route path="attribution" element={<LeadAttributionDashboard />} />
            <Route path="reviews" element={<Navigate to="/integrations/gmb/reviews" replace />} />
            <Route path="data" element={<PlaceholderPage title="Customer Data" />} />
          </Route>

          {/* Marketing & Social Routes */}
          <Route path="marketing">
            <Route path="social" element={<SocialMediaManagerDirect />} />
            <Route path="social/create" element={<CreatePost />} />
            <Route path="calendar" element={<ContentCalendar />} />
            <Route path="campaigns" element={<CampaignTracking />} />
            <Route path="analytics" element={<SocialAnalytics />} />
            <Route path="status" element={<SystemStatus />} />
          </Route>

          {/* Integrations Routes */}
          <Route path="integrations">
            <Route index element={<IntegrationsOverview />} />
            <Route path="jobber" element={<JobberManagement />} />
            <Route path="gmb" element={<GMBLayout />}>
              <Route index element={<GMBOverview />} />
              <Route path="reviews" element={<ReviewsManagement />} />
              <Route path="sync-reviews" element={<GMBReviews />} />
              <Route path="posts" element={<GMBPosts />} />
              <Route path="insights" element={<PlaceholderPage title="GMB Insights" />} />
              <Route path="qa" element={<GMBQuestions />} />
              <Route path="locations" element={<PlaceholderPage title="GMB Locations" />} />
              <Route path="sync" element={<GMBSync />} />
              <Route path="debug" element={<GMBDebug />} />
            </Route>
            <Route path="gmb/setup" element={<GMBSetup />} />
            <Route path="analytics" element={<PlaceholderPage title="Analytics Integration" />} />
          </Route>

          {/* Administration Routes */}
          <Route path="admin">
            <Route path="users" element={<PlaceholderPage title="User Management" />} />
            <Route path="security" element={<PlaceholderPage title="Security & Audit" />} />
            <Route path="settings" element={<PlaceholderPage title="Site Settings" />} />
          </Route>
        </Route>
      </Routes>
    </Router>
  )
}